import { useCallback, useContext, useMemo, useState } from 'react';

import { AgencyApi } from '@/apis';
import { ConfirmTypes } from '@/components';
import { AccountContext } from '@/contexts/AccountContext';
import { NerisContext } from '@/contexts/NerisContext';
import { useAppDispatch } from '@/hooks';
import { AgencyType, EntityStation, IAgency, IAgencyForm } from '@/models';
import { showToast } from '@/store';
import {
  getErrString,
  mapEntityStationsToStationOptions,
  maskToUSNumber,
} from '@/utils';

export interface StationOption {
  label: string;
  id: string;
  name: string;
}

export type CaseType =
  | 'NO_AGENCIES'
  | 'NO_STATIONS'
  | 'EQUAL'
  | 'MORE_STATIONS'
  | 'MORE_AGENCIES';

interface AgencyAssignmentStatus {
  isAssigned: boolean;
  isLocked: boolean;
  stationId?: string;
}

export interface UseNerisStationAssignmentReturn {
  // Computed data
  fireAgencies: IAgencyForm[];
  stations: EntityStation[];
  stationOptions: StationOption[];
  caseType: CaseType;

  // Helper functions for components
  getAvailableStations: (excludeAgencyId?: string) => StationOption[];
  getAgencyAssignmentStatus: (agencyId: string) => AgencyAssignmentStatus;

  // Action handlers
  updateStationAssignment: (agencyId: string, stationId: string) => void;
  updateStationUnassignment: (agencyId: string) => void;
  assignStation: (agencyId: string, stationId: string) => Promise<void>;
  unassignStation: (agencyId: string) => Promise<void>;
  autoCreateStation: (agencyId: string) => Promise<void>;
  refreshEntity: () => Promise<void>;

  // State
  isAssigning: Record<string, boolean>;
  errors: Record<string, string>;

  // Config from contexts
  isNerisEnabled: boolean;
  isNerisConfirmed: boolean;
  accountId?: string;
}

export const useNerisStationAssignment =
  (): UseNerisStationAssignmentReturn => {
    const dispatch = useAppDispatch();
    const { account, agencies = [], updateData } = useContext(AccountContext);
    const { stations, getEntity } = useContext(NerisContext);

    // UI State
    const [isAssigning, setIsAssigning] = useState<Record<string, boolean>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Config from contexts
    const isNerisEnabled = account?.settings?.features?.neris?.enabled || false;
    const isNerisConfirmed = !!(
      account?.settings?.features?.neris?.entityId &&
      account?.settings?.features?.neris?.entityId.length > 0
    );
    const accountId = account?._id;

    // Filter fire agencies
    const fireAgencies = useMemo(
      () => agencies.filter((agency) => agency.type === AgencyType.FIRE),
      [agencies],
    );

    // Transform NERIS entity stations to options
    const stationOptions = useMemo(
      () => mapEntityStationsToStationOptions(stations),
      [stations],
    );

    // Detect case type
    const caseType: CaseType = useMemo(() => {
      const agencyCount = fireAgencies.length;
      const stationCount = stations.length;

      if (agencyCount === 0) return 'NO_AGENCIES';
      if (stationCount === 0) return 'NO_STATIONS';
      if (agencyCount === stationCount) return 'EQUAL';
      if (agencyCount < stationCount) return 'MORE_STATIONS';
      if (agencyCount > stationCount) return 'MORE_AGENCIES';
      return 'EQUAL'; // fallback
    }, [fireAgencies.length, stations.length]);

    // Helper: Get available stations (excluding already assigned ones)
    const getAvailableStations = useCallback(
      (excludeAgencyId?: string): StationOption[] => {
        return stationOptions.filter((opt) => {
          const assignedToOther = fireAgencies.some(
            (agency) =>
              agency._id !== excludeAgencyId &&
              agency.neris?.stationId === opt.id,
          );
          return !assignedToOther;
        });
      },
      [stationOptions, fireAgencies],
    );

    // Helper: Get assignment status for an agency
    const getAgencyAssignmentStatus = useCallback(
      (agencyId: string): AgencyAssignmentStatus => {
        const agency = fireAgencies.find((a) => a._id === agencyId);
        if (!agency) {
          return { isAssigned: false, isLocked: false };
        }

        const stationId = agency.neris?.stationId;
        const isLocked = agency.neris?.hasFireKasesIncidents === true;
        const isAssigned = !!stationId;

        return { isAssigned, isLocked, stationId };
      },
      [fireAgencies],
    );

    // Action: Refresh NERIS entity
    const refreshEntity = useCallback(async () => {
      if (!isNerisConfirmed || !account?.settings?.features?.neris?.entityId) {
        return;
      }
      try {
        await getEntity(account.settings.features.neris.entityId);
      } catch (error) {
        console.error('Error refreshing entity:', error);
        const errorMsg = getErrString(error);
        dispatch(
          showToast({
            message: errorMsg,
            type: ConfirmTypes.ERROR,
          }),
        );
      }
    }, [isNerisConfirmed, account, getEntity, dispatch]);

    /**
     * Action: Update station assignment locally (for form use - no API call)
     * This updates the local context without persisting to backend
     * Useful when the form will handle the API call on submit
     */
    const updateStationAssignment = useCallback(
      (agencyId: string, stationId: string) => {
        const agency = agencies.find((a) => a._id === agencyId);
        if (!agency) {
          dispatch(
            showToast({
              message: 'Agency not found',
              type: ConfirmTypes.ERROR,
            }),
          );
          return;
        }

        // Update local agency state in context (no API call)
        const index = agencies.findIndex((a) => a._id === agencyId);
        if (index > -1) {
          const agencyList = agencies.slice();
          agencyList[index] = {
            ...agencyList[index],
            neris: {
              ...agency.neris,
              stationId,
              hasFireKasesIncidents:
                agency.neris?.hasFireKasesIncidents || false,
            },
          };
          updateData({ agencies: agencyList });
        }

        dispatch(
          showToast({
            message: 'Station selected. Click Save to persist changes.',
            type: ConfirmTypes.SUCCESS,
          }),
        );
      },
      [agencies, dispatch, updateData],
    );

    /**
     * Action: Update station unassignment locally (for form use - no API call)
     * This removes the neris object from local context without persisting to backend
     * Useful when the form will handle the API call on submit
     * Only works if agency has no incidents (hasFireKasesIncidents === false)
     */
    const updateStationUnassignment = useCallback(
      (agencyId: string) => {
        const agency = agencies.find((a) => a._id === agencyId);
        if (!agency) {
          dispatch(
            showToast({
              message: 'Agency not found',
              type: ConfirmTypes.ERROR,
            }),
          );
          return;
        }

        // Check if agency has incidents - cannot unassign if incidents exist
        const hasIncidents = agency.neris?.hasFireKasesIncidents === true;
        if (hasIncidents) {
          dispatch(
            showToast({
              message:
                'This station cannot be unassigned - incident records have been submitted',
              type: ConfirmTypes.ERROR,
            }),
          );
          return;
        }

        // Update local agency state in context (no API call) - remove neris object
        const index = agencies.findIndex((a) => a._id === agencyId);
        if (index > -1) {
          const agencyList = agencies.slice();
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { neris: _neris, ...agencyWithoutNeris } = agencyList[index];
          agencyList[index] = agencyWithoutNeris;
          updateData({ agencies: agencyList });
        }

        dispatch(
          showToast({
            message: 'Station unassigned. Click Save to persist changes.',
            type: ConfirmTypes.SUCCESS,
          }),
        );
      },
      [agencies, dispatch, updateData],
    );

    // Action: Assign station to agency AND persist to API (for list/table use)
    // This makes an API call immediately and updates the local state
    const assignStation = useCallback(
      async (agencyId: string, stationId: string) => {
        const agency = agencies.find((a) => a._id === agencyId);
        if (!agency) {
          dispatch(
            showToast({
              message: 'Agency not found',
              type: ConfirmTypes.ERROR,
            }),
          );
          return;
        }

        const previousStationId = agency?.neris?.stationId;
        const isReassignment =
          previousStationId && previousStationId !== stationId;

        setIsAssigning((prev) => ({ ...prev, [agencyId]: true }));
        setErrors((prev) => {
          const updated = { ...prev };
          delete updated[agencyId];
          return updated;
        });

        try {
          // Check for reassignment and incidents
          if (isReassignment) {
            const response = await AgencyApi.hasNerisIncidents(agencyId);
            const hasIncidents = response.data.hasNerisIncidents;

            if (hasIncidents) {
              const errorMsg =
                'This station is locked - incident records have been submitted and cannot be reassigned';
              setErrors((prev) => ({ ...prev, [agencyId]: errorMsg }));
              dispatch(
                showToast({
                  message: errorMsg,
                  type: ConfirmTypes.ERROR,
                }),
              );
              return;
            }
          }

          // Parse area if it's a string (from form)
          const areaData =
            typeof agency.area === 'string' && agency.area
              ? JSON.parse(agency.area)
              : agency.area;

          // Update agency with new station assignment
          // Note: Keep phoneNumber as-is in database (with + if present)
          // The backend API will normalize it for NERIS when needed
          const updatedAgency = {
            ...agency,
            area: areaData,
            phoneNumber: maskToUSNumber(agency?.phoneNumber ?? ''),
            neris: {
              ...agency.neris,
              stationId,
              hasFireKasesIncidents:
                agency.neris?.hasFireKasesIncidents || false,
            },
          };

          // Persist to API immediately
          await AgencyApi.update(updatedAgency, accountId);

          // Update local agency state in context
          const index = agencies.findIndex((a) => a._id === agencyId);
          if (index > -1) {
            const agencyList = agencies.slice();
            agencyList[index] = {
              ...agencyList[index],
              neris: updatedAgency.neris,
            };
            updateData({ agencies: agencyList });
          }

          dispatch(
            showToast({
              message: 'NERIS station assigned successfully',
              type: ConfirmTypes.SUCCESS,
            }),
          );
        } catch (error) {
          console.error('Error assigning station:', error);
          const errorMsg = 'Failed to assign station';
          setErrors((prev) => ({ ...prev, [agencyId]: errorMsg }));
          dispatch(
            showToast({
              message: errorMsg,
              type: ConfirmTypes.ERROR,
            }),
          );
        } finally {
          setIsAssigning((prev) => ({ ...prev, [agencyId]: false }));
        }
      },
      [agencies, accountId, updateData, dispatch],
    );

    // Action: Unassign station from agency AND persist to API
    // This removes the neris object from the agency if no incidents exist
    const unassignStation = useCallback(
      async (agencyId: string) => {
        const agency = agencies.find((a) => a._id === agencyId);

        if (!agency) {
          dispatch(
            showToast({
              message: 'Agency not found',
              type: ConfirmTypes.ERROR,
            }),
          );
          return;
        }

        // Check if agency has incidents - cannot unassign if incidents exist
        const hasIncidents = agency.neris?.hasFireKasesIncidents === true;
        if (hasIncidents) {
          const errorMsg =
            'This station cannot be unassigned - incident records have been submitted';
          setErrors((prev) => ({ ...prev, agencyId: errorMsg }));
          dispatch(
            showToast({
              message: errorMsg,
              type: ConfirmTypes.ERROR,
            }),
          );
          return;
        }

        setIsAssigning((prev) => ({ ...prev, [agencyId]: true }));
        setErrors((prev) => {
          const updated = { ...prev };
          delete updated[agencyId];
          return updated;
        });

        try {
          // Parse area if it's a string (from form)
          const areaData =
            typeof agency.area === 'string' && agency.area
              ? JSON.parse(agency.area)
              : agency.area;

          // Create updated agency without neris object
          // Remove neris property by destructuring it out
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { neris: _neris, ...agencyWithoutNeris } = agency;
          const unassignedNerisAgency = {
            ...agencyWithoutNeris,
            phoneNumber: maskToUSNumber(agency?.phoneNumber ?? ''),
            area: areaData,
          };

          // Remove undefined properties (including neris if it was undefined) before sending to API
          const cleanedAgency = Object.fromEntries(
            Object.entries(unassignedNerisAgency).filter(
              ([, value]) => value !== undefined,
            ),
          ) as IAgency;

          // Persist to API immediately
          await AgencyApi.update(cleanedAgency, accountId);

          // Update local agency state in context
          const index = agencies.findIndex((a) => a._id === agencyId);
          if (index > -1) {
            const agencyList = agencies.slice();
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { neris: _neris, ...agencyWithoutNeris } = agencyList[index];
            agencyList[index] = {
              ...agencyWithoutNeris,
              area: areaData,
            };
            updateData({ agencies: agencyList });
          }

          dispatch(
            showToast({
              message: 'NERIS station unassigned successfully',
              type: ConfirmTypes.SUCCESS,
            }),
          );
        } catch (error) {
          console.error('Error unassigning station:', error);
          const errorMsg = getErrString(error) || 'Failed to unassign station';
          setErrors((prev) => ({ ...prev, [agencyId]: errorMsg }));
          dispatch(
            showToast({
              message: errorMsg,
              type: ConfirmTypes.ERROR,
            }),
          );
        } finally {
          setIsAssigning((prev) => ({ ...prev, [agencyId]: false }));
        }
      },
      [agencies, accountId, updateData, dispatch],
    );

    // Action: Auto-create single station
    const autoCreateStation = useCallback(
      async (agencyId: string) => {
        setIsAssigning((prev) => ({ ...prev, [agencyId]: true }));
        setErrors((prev) => {
          const updated = { ...prev };
          delete updated[agencyId];
          return updated;
        });

        try {
          const response = await AgencyApi.autoAssignNerisStation(
            agencyId,
            account?._id || '',
          );

          const { station } = response.data;
          // Refresh NERIS entity to get the newly created station
          await refreshEntity();

          // Update local context with the new station assignment
          if (station) {
            const agency = agencies.find((a) => a._id === agencyId);
            if (agency) {
              const index = agencies.findIndex((a) => a._id === agencyId);
              if (index > -1) {
                const agencyList = agencies.slice();
                agencyList[index] = {
                  ...agencyList[index],
                  neris: {
                    ...agency.neris,
                    stationId: station.neris_id,
                    hasFireKasesIncidents: false,
                  },
                };
                updateData({ agencies: agencyList });
              }
            }
          }

          // Note: The API should update the agency, so we refresh from context
          // The entity refresh will trigger a re-render with new data

          dispatch(
            showToast({
              message: 'NERIS station created and assigned successfully',
              type: ConfirmTypes.SUCCESS,
            }),
          );
        } catch (error) {
          console.error('Error creating station:', error);
          const errorMsg = getErrString(error);
          setErrors((prev) => ({ ...prev, [agencyId]: errorMsg }));
          dispatch(
            showToast({
              message: errorMsg,
              type: ConfirmTypes.ERROR,
            }),
          );
        } finally {
          setIsAssigning((prev) => ({ ...prev, [agencyId]: false }));
        }
      },
      [agencies, refreshEntity, updateData, dispatch, account],
    );

    return {
      // Computed data
      fireAgencies,
      stations,
      stationOptions,
      caseType,

      // Helper functions
      getAvailableStations,
      getAgencyAssignmentStatus,

      // Action handlers
      updateStationAssignment,
      updateStationUnassignment,
      assignStation,
      unassignStation,
      autoCreateStation,
      refreshEntity,

      // State
      isAssigning,
      errors,

      // Config
      isNerisEnabled,
      isNerisConfirmed,
      accountId,
    };
  };
